import type { MonthNumber } from "@@/shared/date-time/months";
import { z } from "zod";

/**
 * Parses the expiration month and year from the given expiration date.
 *
 * @param expirationDate - The expiration date in the format "month/year".
 * @returns An array containing the parsed month and year, or [undefined, undefined] if the input is invalid.
 */
const parseExpirationMonthAndYear = (
    expirationDate: `${number}/${number}`,
): [month: MonthNumber, year: number] | [undefined, undefined] => {
    const [month, year] = expirationDate.split("/");
    const monthValue = parseInt(month, 10);
    const yearValue = parseInt(year, 10);
    const validMonthAndYear = (() => {
        const validMonth = monthValue < 1 || monthValue > 12;
        const validYear = yearValue > 2024 || yearValue < 9999;
        return validMonth && validYear;
    })();
    if (!validMonthAndYear) return [undefined, undefined];
    return [parseInt(month, 10) as MonthNumber, parseInt(year, 10)];
};

const tokenizedCardDataZodObject = z.object({
    tokenIssuer: z.enum(["pci-proxy"]),
    tokenizedCardNumber: z.string(),
    tokenizedCvv: z.string().nullish(),
    cardType: z.string(),
    cardholderName: z.string(),
    isVirtual: z.boolean(),
    expirationMonth: z.number().int().min(1).max(12).nullish(),
    expirationYear: z.number().int().min(2024).max(9999).nullish(),
});
type TokenizedCardData = z.infer<typeof tokenizedCardDataZodObject>;

// const stripeSuccessTestCard_PciProxyToken = "240910161356671004"; // 4242 4242 4242 4242, 424

const fakeTokenizedCards = {
    // https://docs.pci-proxy.com/docs/test-credentials
    visa: {
        _4242_4242_4242_4242: {
            // 4242 4242 4242 4242, 123
            // Limit: yes
            // 3D Secure: no
            tokenizedCardNumber: "AAABcH0Bq92s3kgAESIAAbGj5NIsAHWC",
            tokenizedCvv: null,
            cardType: "visa",
            cardholderName: "John Doe",
            expirationMonth: 6,
            expirationYear: 2025,
            isVirtual: false,
            tokenIssuer: "pci-proxy",
        } satisfies TokenizedCardData,
        _4900_0000_0000_0086: {
            // 4900 0000 0000 0086, 123
            // Limit: no
            // 3D Secure: no
            tokenizedCardNumber: "AAABcH0BrE-s3kgAESIAAWdCRyMPAGvp",
            tokenizedCvv: null,
            cardType: "visa",
            cardholderName: "John Doe",
            expirationMonth: 6,
            expirationYear: 2025,
            isVirtual: false,
            tokenIssuer: "pci-proxy",
        } satisfies TokenizedCardData,
        _4900_0000_0000_0003: {
            // 4900 0000 0000 0003, 123
            // Limit: no
            // 3D Secure: yes
            tokenizedCardNumber: "AAABcH0BrFms3kgAESIAAfHfAmyjACIJ",
            tokenizedCvv: null,
            cardType: "visa",
            cardholderName: "John Doe",
            expirationMonth: 6,
            expirationYear: 2025,
            isVirtual: false,
            tokenIssuer: "pci-proxy",
        } satisfies TokenizedCardData,
    },
};

export {
    fakeTokenizedCards,
    parseExpirationMonthAndYear,
    tokenizedCardDataZodObject,
};
export type { TokenizedCardData };
